import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Card } from "react-bootstrap";

export default function StatsTab() {
    const { caisses } = useSelector(state => state.caisses);

    // 1. Répartition des montants par véhicule / propriété
    const pieData = useMemo(() => {
        const map = {};
        caisses.forEach(op => {
            const label = op.vehicle
                ? `${op.vehicle.brand} ${op.vehicle.model}`
                : op.property
                    ? op.property.name
                    : "Autre";
            map[label] = (map[label] || 0) + op.amount;
        });
        return {
            labels: Object.keys(map),
            series: Object.values(map),
        };
    }, [caisses]);

    // 2. Bilan mensuel (total)
    const lineData = useMemo(() => {
        const months = {};
        caisses.forEach(op => {
            const key = moment(op.createdAt).format("YYYY-MM");
            months[key] = (months[key] || 0) + op.amount;
        });

        const sortedEntries = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
        return {
            categories: sortedEntries.map(([month]) =>
                moment(month, "YYYY-MM").format("MMM YY")
            ),
            series: [
                {
                    name: "Total mensuel",
                    data: sortedEntries.map(([, total]) => total),
                },
            ],
        };
    }, [caisses]);

    // 3. Nombre d’opérations par mois
    const barData = useMemo(() => {
        const months = {};
        caisses.forEach(op => {
            const key = moment(op.createdAt).format("YYYY-MM");
            months[key] = (months[key] || 0) + 1;
        });

        const sortedEntries = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
        return {
            categories: sortedEntries.map(([month]) =>
                moment(month, "YYYY-MM").format("MMM YY")
            ),
            series: [
                {
                    name: "Nombre d'opérations",
                    data: sortedEntries.map(([, count]) => count),
                },
            ],
        };
    }, [caisses]);

    // 4. Évolution cumulée des montants (graphique area)
    const cumulativeData = useMemo(() => {
        const months = {};
        caisses.forEach(op => {
            const key = moment(op.createdAt).format("YYYY-MM");
            months[key] = (months[key] || 0) + op.amount;
        });

        const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
        let cumulativeTotal = 0;
        const data = sorted.map(([_, value]) => {
            cumulativeTotal += value;
            return cumulativeTotal;
        });

        return {
            categories: sorted.map(([month]) => moment(month, "YYYY-MM").format("MMM YY")),
            series: [
                {
                    name: "Montant cumulé",
                    data: data,
                },
            ],
        };
    }, [caisses]);

    return (
        <Card>
            <Card.Header>
                <h3>Statistiques</h3>
            </Card.Header>
            <Card.Body>
                <div className="d-flex row">
                    {/* 1. Diagramme circulaire */}
                    <div className=" col-md-6">
                        <h5>Répartition des montants</h5>
                        <ReactApexChart
                            options={{
                                labels: pieData.labels,
                                legend: { position: 'bottom' }
                            }}
                            series={pieData.series}
                            type="pie"
                            width="500"
                        />
                    </div>

                    {/* 2. Ligne du bilan mensuel */}
                    <div className=" col-md-6">
                        <h5>Bilan mensuel</h5>
                        <ReactApexChart
                            options={{
                                chart: { id: "line-bilan" },
                                xaxis: { categories: lineData.categories },
                                stroke: { curve: "smooth" }
                            }}
                            series={lineData.series}
                            type="line"
                            height={300}
                        />
                    </div>

                    {/* 3. Barres : nombre d’opérations */}
                    <div className="col-md-6">
                        <h5>Évolution du nombre d’opérations</h5>
                        <ReactApexChart
                            options={{
                                chart: { id: "bar-operations" },
                                xaxis: { categories: barData.categories },
                                dataLabels: { enabled: false },
                            }}
                            series={barData.series}
                            type="bar"
                            height={300}
                        />
                    </div>

                    {/* 4. Courbe de montant cumulé */}
                    <div className="col-md-6">
                        <h5>Évolution du montant cumulé</h5>
                        <ReactApexChart
                            options={{
                                chart: { id: "area-cumul" },
                                xaxis: { categories: cumulativeData.categories },
                                stroke: { curve: "smooth" },
                                dataLabels: { enabled: false },
                            }}
                            series={cumulativeData.series}
                            type="area"
                            height={300}
                        />
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
}
